import { PlansScreen } from '../../../../../analytics/plansEvents'
import { useRef } from 'react'
import type { LumpsumRebalanceSellPlanRecommendationDetails } from '../../../api/rebalanceRecommendation.types'
import type { FundMissedGainsInfoParams } from '../../../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import { buildRebalanceSellRecommendationPair } from '../../../pair/buildPlanRecommendationPair'
import { ReasonsToRebalanceAccordion } from '../../../shared/reasons/ReasonsToRebalanceAccordion'
import { StepsToRebalanceAccordion } from '../../../shared/steps/StepsToRebalanceAccordion'
import { PlanTaxLiability } from '../../../shared/tax/PlanTaxLiability'
import { PlanUnderlineTabs } from '../../../shared/sheet/PlanUnderlineTabs'
import { useScrollToFirstAccordionOnTabChange } from '../../../shared/sheet/planRecommendationSheetScroll'

type RebalanceSellPlanRecommendationBodyProps = {
  planDetails: LumpsumRebalanceSellPlanRecommendationDetails | null
  selectedBuyIndex: number
  onSelectBuyIndex: (selectedIndex: number) => void
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
}

export function RebalanceSellPlanRecommendationBody({
  planDetails,
  selectedBuyIndex,
  onSelectBuyIndex,
  onMissedGainsInfoClick
}: RebalanceSellPlanRecommendationBodyProps) {
  const buyFunds = planDetails?.fundsToBeBought ?? []
  const stickyTabsRef = useRef<HTMLDivElement>(null)
  const firstAccordionRef = useRef<HTMLDivElement>(null)
  useScrollToFirstAccordionOnTabChange(stickyTabsRef, firstAccordionRef, selectedBuyIndex)

  const taxData = planDetails?.consolidatedTax ?? buyFunds[0]?.taxData ?? null
  const areAllSellsCompleted = planDetails?.executed === true
  // Outside tax uses full-sheet redeem (root totalTransferAmount), not per-leg pair amounts.
  const taxWithdrawalAmount = showAsFormattedRupeeAmount({
    rawAmount: planDetails?.totalTransferAmount ?? 0
  }).value
  // 1:1 → root side; 1:m → counter (buy) side
  const detailsTitle = buyFunds.length > 1 ? 'Buy details' : 'Sell details'

  return (
    <>
      <div className="h-[10px] bg-[#F0F4F7] mt-6" />

      <div ref={stickyTabsRef} className="sticky top-0 z-[1] bg-white pt-5">
        <div className="px-5 pb-6 pt-2 text-[18px] font-[500] leading-[130%] text-[#232323]">
          {detailsTitle}
        </div>
        <PlanUnderlineTabs
          analyticsScreen={PlansScreen.LumpsumRebalanceRecommendation}
          funds={buyFunds.map((fund) => ({
            fundName: fund?.fundName ?? '',
            fundIsin: fund?.isin ?? '',
            mfLogoUrl: fund?.mfLogoUrl ?? ''
          }))}
          activeTabIndex={selectedBuyIndex}
          onChange={onSelectBuyIndex}
        />
      </div>

      <div ref={firstAccordionRef} aria-hidden />

      {buyFunds.map((buyFund, buyFundIndex) => {
        const pair = buildRebalanceSellRecommendationPair(planDetails, buyFund)
        if (!pair) {
          return null
        }

        return (
          <div
            key={buyFund?.isin ?? String(buyFundIndex)}
            className={buyFundIndex === selectedBuyIndex ? undefined : 'hidden'}
            aria-hidden={buyFundIndex !== selectedBuyIndex}
          >
            <ReasonsToRebalanceAccordion
              analyticsScreen={PlansScreen.LumpsumRebalanceRecommendation}
              pair={pair}
              onMissedGainsInfoClick={onMissedGainsInfoClick}
            />
            <StepsToRebalanceAccordion
              pair={pair}
              analyticsScreen={PlansScreen.LumpsumRebalanceRecommendation}
            />
          </div>
        )
      })}

      <PlanTaxLiability
        isOutside
        areAllSellActionsCompleted={areAllSellsCompleted}
        fundName={planDetails?.fundName ?? ''}
        fundIsin={planDetails?.isin ?? ''}
        tax={{
          taxData,
          withdrawalAmount: taxWithdrawalAmount,
          currentValue: planDetails?.currentValue ?? taxData?.currentValue ?? null
        }}
      />
    </>
  )
}
